// Mirrors btclock-api's src/btclock_api/schemas.py field-for-field. This
// dashboard never renames/reshapes these -- if the API's shape changes,
// this file should be the only place that needs to change to match it.

export interface Tick {
  id: number;
  ts_utc: string;
  ratio: number | null; // null when in-band bid depth was 0 (undefined, not fabricated 0)
  // T-156 (2026-08-21): TRUE per-second ratio extremes across every book
  // state in that second. Null on rows before the column existed; optional
  // (absent) until the backend deploys. Invariant when present:
  // ratio_window_min <= ratio <= ratio_window_max.
  ratio_window_max?: number | null;
  ratio_window_min?: number | null;
  mid_price: number;
  best_bid: number;
  best_ask: number;
  // ADR-0013: null when `entitled` is false (server-side redaction) -- the
  // null + entitled:false pair IS the locked signal, never a fake 0.
  bid_depth: number | null;
  ask_depth: number | null;
  band_width_usd: number;
  ws_sequence_num: number | null;
  book_bid_levels: number;
  book_ask_levels: number;
  // ADR-0013 entitlement flag. Optional: absent until the backend's
  // redaction deploys; a payload without it is treated as entitled=true
  // (see api/entitlement.ts's isEntitled for the full rationale).
  entitled?: boolean;
}

export interface RollupBar {
  period_start_utc: string;
  open_ratio: number | null;
  high_ratio: number | null;
  low_ratio: number | null;
  close_ratio: number | null;
  avg_ratio: number | null;
  bullish_avg_ratio: number | null;
  bearish_avg_ratio: number | null;
  open_mid_price: number;
  high_mid_price: number;
  low_mid_price: number;
  close_mid_price: number;
  tick_count: number;
}

export type HistoryResolution = "raw" | "1m" | "15m" | "1h";

export interface HistoryResponse {
  window_minutes: number;
  since_ts_utc: string;
  count: number;
  truncated: boolean;
  resolution: HistoryResolution;
  ticks: Tick[];
  bars: RollupBar[];
}

export interface DailyExtremesResponse {
  day_utc: string;
  // ADR-0013: all six hod_/lod_ fields are additionally null when
  // `entitled` is false (server-side redaction), on top of their existing
  // "no ticks with a defined ratio yet today" null case.
  hod_ratio: number | null;
  hod_mid_price: number | null;
  hod_ts_utc: string | null;
  lod_ratio: number | null;
  lod_mid_price: number | null;
  lod_ts_utc: string | null;
  // ADR-0013 entitlement flag; absent = entitled (transitional, see
  // api/entitlement.ts's isEntitled).
  entitled?: boolean;
}

export interface DailyVolumeSnapshot {
  day_utc: string;
  volume_usd: number;
  volume_btc: number;
  snapshotted_at_utc: string;
}

export interface VolumeHistoryResponse {
  days: number;
  count: number;
  snapshots: DailyVolumeSnapshot[]; // most-recent-day-first (DESC), same as legacy
}

export interface Coinbase24hStatsResponse {
  price_24h_high: number | null;
  price_24h_low: number | null;
  volume_24h_btc: number | null;
  volume_24h_usd: number | null;
  fetched_at_utc: string | null;
}

export interface HealthResponse {
  status: string;
  ingest_db_reachable: boolean;
  latest_tick_ts_utc: string | null;
  seconds_since_latest_tick: number | null;
  server_time_utc: string;
}

export type AlertDirection = "above" | "below";

export interface AlertCreateRequest {
  direction: AlertDirection;
  value: number;
  cooldown_seconds?: number;
}

export interface AlertResponse {
  id: number;
  direction: AlertDirection;
  value: number;
  cooldown_seconds: number;
  created_at_utc: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  /** First-party marketing opt-in (default false server-side if omitted). */
  marketing_opt_in?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  // SW-3 device cap: stable per-browser UUID + friendly name, both optional
  // on the wire (older API ignores them); auth.ts's login() always sends
  // them.
  device_id?: string;
  device_name?: string;
}

// SW-22 devices (GET /v1/devices, FIXED contract): sorted last_seen desc;
// `current` marks the device the bearer token was issued to.
export interface DeviceRecord {
  device_id: string;
  device_name: string | null;
  created_at_utc: string;
  last_seen_utc: string | null;
  current: boolean;
}

export interface DevicesResponse {
  devices: DeviceRecord[];
  limit: number;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
}

// Mirrors btclock-api's SubscriptionStatusResponse
// (routes/subscription.py, GET /v1/subscription/status). `admin` is the
// role-tier flag: admins get full/unrestricted /v1/history (the backend
// bypasses the 48h per-request window clamp + the daily coverage budget for
// them), so the chart may offer them the full period range. Additive on the
// backend, so `admin` is optional here for forward/backward compatibility --
// an older API without the field reads as non-admin (the safe, restricted
// default), exactly matching how a standard subscriber is treated.
export interface SubscriptionStatusResponse {
  state: string;
  entitled: boolean;
  admin?: boolean;
  source: string | null;
  productId: string | null;
  currentPeriodEnd: string | null;
  gracePeriodEnd: string | null;
}

export type Timeframe = "5m" | "15m" | "1h" | "4h";

export const TIMEFRAME_MINUTES: Record<Timeframe, number> = {
  "5m": 5,
  "15m": 15,
  "1h": 60,
  "4h": 240,
};

export const KNOWN_BAND_WIDTHS_USD = [2400, 3600, 5000] as const;
export const DEFAULT_BAND_WIDTH_USD = 2400;
